<template>
<div class="form-wrapper">
    <div class="confirmation">
        <card-header :title="'Welcome!'" :designVersion="2" :subtitle="'Add your info to make collaborating easy'" />
        <div class="body">
            <div class="spinner" :class="{'hidden': !isSubmitting}"><i class="fa fa-spinner fa-pulse"></i></div>
            <div class="form-group" :class="{'hidden': isSubmitting}">
                <input-set ref="firstName"
                    v-model="firstName"
                    :type="'text'"
                    :name="'first-name'"
                    :label="errors.firstName ? errors.firstName : ''"
                    :placeholder="'First name*'"
                    :hasError="errors.firstName ? true : false"
                    @inputOnEnter="signUp"
                    @inputOnBlur="checkOnInputBlur"
                    @inputOnType="checkOnInputType" >
                </input-set>
                <input-set v-model="lastName" :type="'text'" :name="'last-name'" :label="errors.lastName ? errors.lastName : ''" :placeholder="'Last name*'" @inputOnEnter="signUp" @inputOnBlur="checkOnInputBlur" @inputOnType="checkOnInputType" :hasError="errors.lastName ? true : false"></input-set>
                <input-set
                    v-model="industry"
                    :isDropdown="true"
                    :type="'text'"
                    :name="'industry'"
                    :label="errors.industry ? errors.industry : ''"
                    :placeholder="'Industry*'"
                    :selected="industry"
                    :entries="industries"
                    :hasError="errors.industry ? true : false"
                    @inputOnEnter="signUp"
                    @inputOnBlur="checkOnInputBlur"
                    @onDropdownChange="onIndustryChange"
                    @inputOnFocus="onIndustryFocus"
                ></input-set>
                <input-set ref="phoneNumber" v-model="phoneNumberInput" :type="'phone'" :name="'phone-number'" :label="errors.phoneNumber ? errors.phoneNumber : ''" :placeholder="'Phone number*'" @inputOnEnter="phoneNumberInput ? signUp : () => {}" @inputOnBlur="checkOnInputBlur" @inputOnType="checkOnInputType" :hasError="errors.phoneNumber ? true : false"></input-set>
                <div class="button-wrapper">
                    <custom-button v-if="!isSubmitting" :text="'Start using Autobahn'" :isActive="buttonState" @enter="signUp" @click="signUp" :size="'button-large'"></custom-button>
                    <button v-else id="mock-button-to-maintain-form-height" class="btn btn-primary hidden" :isActive="false">Start using Autobahn</button>
                </div>
            </div>
        </div>
    </div>
</div>
</template>

<script>
/* eslint-disable import/extensions */
import '~/assets/js/global.js';
import _ from 'lodash';
import intlTelInput from 'intl-tel-input';
import CardHeader from './card-header.vue';
import Validate from '~/mixins/validation.js';
import PasswordInputWithSuggestion from '~/components/shared/password-input-with-suggestion.vue';
import { signUpEuCentral } from '~/assets/js/utils/ab-aws-migration.js';
import Heading from '~/components/shared/heading.vue';
import Paragraph from '~/components/shared/paragraph.vue';
import InputSet from '~/components/shared/input-set.vue';
import CustomButton from '~/components/shared/custom-button.vue';
import { newIndustries } from '~/assets/constants/industries.js';

export default {
    components: {
        // eslint-disable-next-line vue/no-unused-components
        CardHeader, Heading, Paragraph, InputSet, CustomButton, PasswordInputWithSuggestion,
    },
    mixins: [Validate],
    data() {
        return {
            firstName: '',
            lastName: '',
            dialCode: '1',
            itiPhone: null,
            phoneNumberInput: '',
            isSubmitting: false,
            showPassword: false,
            strength: 0,
            emptyFieldErrorMessage: 'This field cannot be empty',
            industry: '',
            industries: newIndustries,
        };
    },
    props: {
        userData: Object,
    },
    computed: {
        buttonState() {
            let state = this.firstName && this.lastName && this.industry && this.phoneNumber;
            state = state && _.values(this.errors).every((error) => error === '');
            return !!state;
        },
        phoneNumber() {
            const finalPhoneNumber = this.phoneNumberInput ? `+${this.dialCode.toString()}${this.phoneNumberInput.toString()}` : '';
            return finalPhoneNumber;
        },
    },
    methods: {
        checkboxClass(state) {
            const icon = state ? 'check-' : '';
            const fill = state ? 'fa' : 'far';
            return `${fill} fa-${icon}square`;
        },
        createHubspotContact() {
            const payload = {
                email: this.userData.email,
                first_name: this.userData.firstName,
                last_name: this.userData.lastName,
                phone_number: this.userData.phoneNumber,
                industry: this.userData.industry,
                form_used: 'Signup Form',
            };
            this.doBackendRequest(
                'POST',
                '/api/create_hubspot_contact_without_login',
                undefined,
                JSON.stringify(payload),
                undefined,
            );
        },
        authSignUp() {
            const self = this;
            const payload = {
                email: self.userData.email,
                password: self.userData.password,
                firstName: self.userData.firstName,
                lastName: self.userData.lastName,
                industry: self.userData.industry,
                phoneNumber: self.userData.phoneNumber,
            };
            signUpEuCentral(
                payload,
                () => {
                    self.$emit('verify_account', [self.userData.email, self.userData.password]);
                    self.createHubspotContact();
                },
                (err) => {
                    if (err.code === 'UserLambdaValidationException') {
                        self.errors.firstName = 'Email domain is not allowed. You must use a company email.';
                    } else if (err.code === 'NetworkError') {
                        self.errors.firstName = 'Request to Amazon Cognito failed. NoScript and AdBlockers may be blocking requests necessary for signup';
                    } else {
                        self.errors.firstName = 'There was an error creating your account.';
                    }
                    self.isSubmitting = false;
                },
            );
        },
        signUp() {
            const self = this;
            const state = self.checkErrors();
            const { email } = self.userData;
            if (typeof posthog !== 'undefined') {
                // eslint-disable-next-line no-undef
                posthog.identify(email);
            }
            if (!state) {
                return;
            }
            this.isSubmitting = true;
            self.userData.firstName = self.firstName;
            self.userData.lastName = self.lastName;
            self.userData.industry = self.industry;
            self.userData.phoneNumber = self.phoneNumber;
            this.doBackendRequest(
                'GET',
                `/api/check_user_account?email=${email}`,
                (res) => {
                    if (res) {
                        self.$emit('next_index', 2);
                    } else {
                        this.authSignUp();
                    }
                },
                undefined,
                () => {
                    this.errors.firstName = 'There was an error creating your account.';
                    self.isSubmitting = false;
                },
            );
        },
        validIcon(state) {
            return state ? 'fas fa-check-circle text-success' : 'fas fa-times-circle text-primary';
        },
        checkFirstName() {
            return this.validate('firstName', ['req']);
        },
        checkLastName() {
            return this.validate('lastName', ['req']);
        },
        checkIndustry() {
            return this.validate('industry', ['req']);
        },
        checkPhoneNumber() {
            const checkResult = this.validate('phoneNumber', ['phone']);
            if (this.errors.phoneNumber) {
                this.errors.phoneNumber = 'Please enter a valid phone number';
            }
            return checkResult;
        },
        checkErrors() {
            let state = true;
            state = this.checkFirstName() && state;
            state = this.checkLastName() && state;
            state = this.checkIndustry() && state;
            state = this.checkPhoneNumber() && state;
            return state;
        },
        checkOnInputType(name) {
            switch (name) {
            case 'first-name':
                if ('firstName' in this.errors) {
                    this.checkFirstName();
                }
                break;
            case 'last-name':
                if ('lastName' in this.errors) {
                    this.checkLastName();
                }
                break;
            case 'phone-number':
                this.dialCode = this.itiPhone.getSelectedCountryData().dialCode;
                this.checkPhoneNumber();
                break;
            default:
                break;
            }
        },
        onIndustryChange(name, selected) {
            this.industry = selected;
            this.$nextTick(() => {
                if ('industry' in this.errors) {
                    this.checkIndustry();
                }
            });
        },
        onIndustryFocus() {
            this.errors.industry = '';
        },
        checkOnInputBlur(name) {
            switch (name) {
            case 'first-name':
                if (!this.errors.firstName) {
                    this.checkFirstName();
                }
                break;
            case 'last-name':
                if (!this.errors.lastName) {
                    this.checkLastName();
                }
                break;
            case 'industry':
                if (!this.errors.industry) {
                    this.checkIndustry();
                }
                break;
            case 'phone-number':
                if (!this.errors.phoneNumber) {
                    this.checkPhoneNumber();
                }
                break;
            default:
                break;
            }
        },
    },
    mounted() {
        this.$nextTick(() => {
            this.$refs.firstName.$refs.inputSet.focus();

            this.itiPhone = intlTelInput(this.$refs.phoneNumber.$refs.inputSet, {
                separateDialCode: true,
                initialCountry: 'auto',
                geoIpLookup: (callback) => {
                    this.doBackendRequest(
                        'GET',
                        '/api/get_user_location_by_ip',
                        (res) => {
                            if (res.response === 'OK') {
                                callback(res.country_code);
                            }
                        },
                        undefined,
                        () => {
                            callback('us');
                        },
                    );
                },
            });
            this.$refs.phoneNumber.$refs.inputSet.addEventListener('countrychange', () => {
                this.dialCode = this.itiPhone.getSelectedCountryData().dialCode;
            });
            document.getElementsByClassName('iti__selected-flag')[0].setAttribute('tabindex', 1);
        });
    },
};
</script>

<style lang="scss" scoped>
@import '~/scss/pages/signup.scss';
</style>
